import { useId, useMemo, useState } from "react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@monorepo-apps/ui/src/components/dialog";
import { Input } from "@monorepo-apps/ui/src/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";

import {
  useCreateWorkstream,
  useMilestones,
  useWorkstreams,
} from "@/application/use-cases/board/use-board.queries";
import type { WorkstreamRow } from "@/infrastructure/supabase/database.types";

const NO_MILESTONE = "__none__";

/** `block-<slug>` from the code, made unique against the existing workstream keys. */
function deriveKey(code: string, existing: WorkstreamRow[]): string {
  const slug =
    code
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "ws";
  const base = `block-${slug}`;
  const taken = new Set(existing.map((w) => w.key));
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

interface AddWorkstreamDialogProps {
  open: boolean;
  onClose: () => void;
}

export function AddWorkstreamDialog({ open, onClose }: AddWorkstreamDialogProps) {
  const workstreamsQ = useWorkstreams();
  const milestonesQ = useMilestones();
  const create = useCreateWorkstream();

  const [code, setCode] = useState("");
  const [title, setTitle] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [milestone, setMilestone] = useState<string>(NO_MILESTONE);

  const codeId = useId();
  const titleId = useId();
  const startId = useId();
  const endId = useId();
  const milestoneId = useId();

  const workstreams = useMemo(() => workstreamsQ.data ?? [], [workstreamsQ.data]);
  const milestones = milestonesQ.data ?? [];

  // A partial date pair (one set, one blank) is invalid — a lane needs both to be
  // placed, so require either both or neither. `end < start` compares ISO strings.
  const datesInvalid = (!!start && !end) || (!start && !!end) || (!!start && !!end && end < start);
  // `code` drives the board group label ("Block {code}"); reject a duplicate so two
  // groups can't display the same code (the key is de-duped separately).
  const codeTaken = workstreams.some(
    (w) => w.code.trim().toLowerCase() === code.trim().toLowerCase()
  );
  const canSubmit =
    !!code.trim() && !codeTaken && !!title.trim() && !datesInvalid && !create.isPending;

  const reset = () => {
    setCode("");
    setTitle("");
    setStart("");
    setEnd("");
    setMilestone(NO_MILESTONE);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = () => {
    if (!canSubmit) return;
    const maxSort = workstreams.reduce((m, w) => Math.max(m, w.sort_order), -1);
    create.mutate(
      {
        key: deriveKey(code, workstreams),
        code: code.trim(),
        title: title.trim(),
        summary: null,
        target_milestone_key: milestone === NO_MILESTONE ? null : milestone,
        start_date: start || null,
        end_date: end || null,
        sort_order: maxSort + 1,
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New workstream</DialogTitle>
          <DialogDescription>
            Adds a lane to the timeline and a group on the delivery board. Leave the dates blank to
            list it as unscheduled.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className="grid grid-cols-[6rem_1fr] gap-3">
            <Field label="Code" htmlFor={codeId} required>
              <Input
                id={codeId}
                aria-required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. 2i"
              />
            </Field>
            <Field label="Title" htmlFor={titleId} required>
              <Input
                id={titleId}
                aria-required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Returns & refunds"
              />
            </Field>
          </div>
          {codeTaken ? (
            <p className="text-destructive text-[11px]">
              Code “{code.trim()}” is already used by another workstream.
            </p>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date" htmlFor={startId}>
              <Input
                id={startId}
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </Field>
            <Field label="End date" htmlFor={endId}>
              <Input id={endId} type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </Field>
          </div>
          {datesInvalid ? (
            <p className="text-destructive text-[11px]">
              Set both dates (end on/after start) — or leave both blank.
            </p>
          ) : null}
          <Field label="Target milestone" htmlFor={milestoneId}>
            <Select value={milestone} onValueChange={setMilestone}>
              <SelectTrigger id={milestoneId}>
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_MILESTONE}>None</SelectItem>
                {milestones.map((m) => (
                  <SelectItem key={m.key} value={m.key}>
                    {m.code} · {m.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {create.isError ? (
            <p className="text-destructive text-[11px]">Could not create the workstream.</p>
          ) : null}
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSubmit}>
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  htmlFor,
  required,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="text-foreground text-xs font-medium">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </label>
      {children}
    </div>
  );
}
